import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import log from 'loglevel';

import { useGenerateDoBoxFromCommunication } from '../../services/generateDoBox.service';
import { useCommunicationIntentsStore } from '../../store/Inbox/CommunicationIntents';
import {
  isEmptyContentForDoBoxGenerationError,
  isInvalidLabelForDoBoxGenerationError,
} from '../../utils/graphqlErrors';
import { useCustomToast } from '../useCustomToast';

const resolveErrorI18nKey = (err: unknown): string => {
  if (isInvalidLabelForDoBoxGenerationError(err)) {
    return 'components.inboxPreview.header.generateDoBoxInvalidLabelError';
  }
  if (isEmptyContentForDoBoxGenerationError(err)) {
    return 'components.inboxPreview.header.generateDoBoxEmptyContentError';
  }
  return 'components.inboxPreview.header.generateDoBoxError';
};

export const useGenerateDoBox = () => {
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { setLinkedToEntryIntent, clearIntent, settleIntent } =
    useCommunicationIntentsStore();

  const { generateDoBoxFromCommunication } =
    useGenerateDoBoxFromCommunication();

  const executeGenerate = useCallback(
    async (messageId: string, taskGroupId: string): Promise<void> => {
      const entry = await generateDoBoxFromCommunication(
        messageId,
        taskGroupId,
      );

      if (!entry) {
        throw new Error('generateDoBoxFromCommunication returned no entry');
      }
    },
    [generateDoBoxFromCommunication],
  );

  const handleGenerateDoBox = useCallback(
    async (messageId: string, taskGroupId: string): Promise<void> => {
      setIsGenerating(true);
      setError(null);

      setLinkedToEntryIntent({ messageId });

      try {
        await promiseToast(
          executeGenerate(messageId, taskGroupId),
          t('components.inboxPreview.header.generateDoBoxLoading'),
          t('components.inboxPreview.header.generateDoBoxSuccess'),
          (err) => t(resolveErrorI18nKey(err)),
        );

        settleIntent(messageId);
      } catch (err) {
        clearIntent(messageId);

        const errorObj =
          err instanceof Error ? err : new Error('Failed to generate DoBox');
        setError(errorObj);

        log.error('Error generating DoBox:', err);

        throw errorObj;
      } finally {
        setIsGenerating(false);
      }
    },
    [
      t,
      promiseToast,
      setLinkedToEntryIntent,
      clearIntent,
      settleIntent,
      executeGenerate,
    ],
  );

  return {
    handleGenerateDoBox,
    isGenerating,
    error,
  };
};
